import { Box, useTheme } from '@mui/material'
import Konva from 'konva'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Circle, Group, Image, Layer, Stage } from 'react-konva'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import useImage from 'use-image'

import cropicon from '@/assets/images/Crop.svg'
import imageBg from '@/assets/images/ImageBg.svg'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import { useAppSelector } from '@/store'
import { useLazyGetImagePresignedUrlQuery } from '@/store/apiService'
import type { LABColor } from '@/store/appSlice'
import {
  PLACEMENT,
  setCanvasScale,
  setImageEdit,
  setImagePixelInfo,
  setNewImages,
  setSelectedColor,
} from '@/store/appSlice'
import {
  ImageCalibrationStatus,
  RecipeGenerationStatus,
} from '@/store/sessionSlice'
import {
  DUAL_CANVAS_INCLUDED_PATHS,
  getSupportedImagetypes,
} from '@/utils/constants'

import Button from '../Button'
import KonavaElements from '../konva/KonavaElements'
import { getAverageColor } from '../konva/utils'
import type { CanvasAlignment } from './constants'

interface CanvasProps {
  imageUrl?: string
  width: number
  height: number
  alignment?: CanvasAlignment
  onImageClick?: (
    type: string,
    placement: PLACEMENT,
    imagePos: { x: number; y: number },
    hiddenCanvasRef: any,
    labColor: LABColor,
  ) => void
  placement?: PLACEMENT
  type?: string
  imageId?: number | string
  combinedRef?: any
  rawWidth?: number
  rawHeight?: number
  konvaElements?: any
  isActiveCircle?: boolean
  circlePos?: { x: number; y: number } | null
  setCirclePos?: React.Dispatch<
    React.SetStateAction<{ x: number; y: number } | null>
  >
  circleColor?: string
  setCircleColor?: React.Dispatch<React.SetStateAction<string>>
  activeCanvas?: PLACEMENT | null
  setActiveCanvas?: React.Dispatch<React.SetStateAction<PLACEMENT | null>>
  onHoverChange?: (hovering: boolean) => void
}

const NewKonvaCanvas: React.FC<CanvasProps> = ({
  imageUrl = '',
  width,
  height,
  onImageClick,
  placement = PLACEMENT.CENTER,
  type = '',
  imageId,
  combinedRef,
  rawWidth = 0,
  rawHeight = 0,
  konvaElements,
  isActiveCircle = false,
  circlePos,
  setCirclePos,
  circleColor,
  setCircleColor,
  activeCanvas = PLACEMENT.CENTER,
  setActiveCanvas = () => {},
  onHoverChange = () => {},
}) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const location = useLocation()
  const imageRef = useRef<Konva.Image | null>(null)

  const [getImagePresignedUrl] = useLazyGetImagePresignedUrlQuery()

  const [image] = useImage(imageUrl, 'anonymous')
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  // const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  const [localCirclePos, setLocalCirclePos] = useState<{
    x: number
    y: number
  } | null>(null)
  const [localCircleColor, setLocalCircleColor] = useState<string>('#ffffff')

  const activeCirclePos = circlePos ?? localCirclePos
  const activeCircleColor = circleColor ?? localCircleColor
  const updateCirclePos = setCirclePos ?? setLocalCirclePos
  const updateCircleColor = setCircleColor ?? setLocalCircleColor

  const stageRef = useRef<any>(null)
  const hiddenCanvasRef = useRef<HTMLCanvasElement | null>(null)

  const isGrayscaleMap = useAppSelector(
    (state) => state.shadeGuide.isGrayscale,
  ) as Record<string, boolean>
  const currentImageGrayscale = imageId ? isGrayscaleMap?.[imageId] : false
  const isShadeGuidePath = useMemo(
    () => location.pathname.includes('/shade-guide-ai'),
    [location.pathname],
  )

  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )

  const recipeGenerationStatus = useAppSelector(
    (state) => state.session.recipeGenerationStatus,
  )
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[location.pathname] || false,
  )
  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)
  const canvasImage = useAppSelector((state) => state.app.canvasImage)
  const scale = useAppSelector((state) => state?.app?.canvasScale?.[placement])
  const isDualCanvas = DUAL_CANVAS_INCLUDED_PATHS.includes(location.pathname)
  const currentCaseId = new URLSearchParams(location.search).get('caseId')

  const handleImageUpload = async (files: File[]) => {
    try {
      const uploads = await Promise.all(
        files.map(async (file) => {
          const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, ' ')
          const presignedData = await getImagePresignedUrl({
            caseId: currentCaseId,
            fileName: sanitizedFileName,
          }).unwrap()
          return { presignedData, file }
        }),
      )

      toast.info('Uploading started.')
      dispatch(setNewImages(uploads))
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed')
    }
  }

  const onDrop = (acceptedFiles: File[]) => {
    const validFiles: File[] = []
    acceptedFiles.forEach((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase()
      const isValid =
        ext && getSupportedImagetypes(location.pathname).includes(ext)
      if (!isValid) {
        toast.error(`Invalid file type: ${file.name}`)
        return
      }
      validFiles.push(file)
    })

    if (validFiles.length > 0) {
      handleImageUpload(validFiles)
    }
  }

  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  useEffect(() => {
    if (!image) return

    let fittedWidth: number
    let fittedHeight: number
    let newX: number
    let newY: number

    const calculateYPosition = (
      isDualCanvas: boolean,
      type: string,
      height: number,
      fittedHeight: number,
    ): number => {
      if (isDualCanvas) {
        return 0
      }
      if (type === imageType.RAW) {
        return 0
      }
      return (height - fittedHeight) / 2
    }

    if (type === imageType.OPTISHADE) {
      const scaleX = width / image.width
      const scaleY = height / image.height
      const newScale = Math.min(scaleX, scaleY)

      fittedWidth = image.width * newScale
      fittedHeight = image.height * newScale
      newX = (width - fittedWidth) / 2
      newY = calculateYPosition(isDualCanvas, type, height, fittedHeight)
    } else {
      const scaleX = width / image.width
      const scaleY = height / image.height
      const newScale = Math.max(scaleX, scaleY)

      fittedWidth = image.width * newScale
      fittedHeight = image.height * newScale

      newX = (width - fittedWidth) / 2
      newY = (height - fittedHeight) / 2
    }

    const canvas = document.createElement('canvas')
    canvas.width = fittedWidth
    canvas.height = fittedHeight
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.drawImage(image, 0, 0, fittedWidth, fittedHeight)
    }
    hiddenCanvasRef.current = canvas

    dispatch(setCanvasScale({ placement, scale: 1 }))
    setPosition({ x: newX, y: newY })
    setImageSize({ width: fittedWidth, height: fittedHeight })
  }, [image, width, height, type, isDualCanvas, dispatch, placement])

  const handleWheel = (e: any) => {
    if (!imageUrl) {
      return
    }
    e.evt.preventDefault()
    const stage = e.target.getStage()
    const oldScale = scale
    const pointer = stage.getPointerPosition()
    const mousePointTo = {
      x: (pointer.x - position.x) / oldScale,
      y: (pointer.y - position.y) / oldScale,
    }
    const newScale = e.evt.deltaY > 0 ? oldScale * 0.95 : oldScale * 1.05
    // setScale(newScale)
    dispatch(setCanvasScale({ placement, scale: newScale }))
    setPosition({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    })
  }

  const handleClick = (e: any) => {
    if (!imageUrl || !image) {
      return
    }
    const stage = e.target.getStage()
    const pointer = stage.getPointerPosition()
    // Convert pointer position to image coordinates
    const imageX = (pointer.x - position.x) / scale
    const imageY = (pointer.y - position.y) / scale

    const { labColor: sampledLab } = getAverageColor(
      imageX,
      imageY,
      1,
      1,
      hiddenCanvasRef,
    )

    const labColor =
      currentImageGrayscale && isShadeGuidePath
        ? { ...sampledLab, a: '00.00', b: '00.00' }
        : sampledLab

    const scaleX = width / image.width
    const scaleY = height / image.height
    const newScale = Math.min(scaleX, scaleY)

    const fittedWidth = image.width * newScale
    const fittedHeight = image.height * newScale

    const newImageX = (Number(rawWidth) / Number(fittedWidth)) * imageX

    const newImageY = (Number(rawHeight) / Number(fittedHeight)) * imageY

    dispatch(
      setImagePixelInfo({
        x: newImageX,
        y: newImageY,
        labColor: labColor as LABColor,
      }),
    )

    onImageClick?.(
      type,
      placement,
      { x: imageX, y: imageY },
      hiddenCanvasRef,
      labColor,
    )
  }
  const handleMouseMove = (e: any) => {
    if (!imageUrl) {
      return
    }
    const stage = e.target.getStage()
    const pointer = stage.getPointerPosition()

    // Convert pointer position to image coordinates
    const imageX = (pointer.x - position.x) / scale
    const imageY = (pointer.y - position.y) / scale

    if (isActiveCircle) {
      updateCirclePos({ x: imageX, y: imageY })
      setActiveCanvas(placement)
    }

    // Get color at the current position
    const { labColor: sampledLab } = getAverageColor(
      imageX,
      imageY,
      1,
      1,
      hiddenCanvasRef,
    )

    const labColor =
      currentImageGrayscale && isShadeGuidePath
        ? { ...sampledLab, a: '00.00', b: '00.00' }
        : sampledLab

    dispatch(setSelectedColor(labColor))
    updateCircleColor(
      `lab(${labColor?.L === '00.00' ? 100 : labColor?.L || 100} ${
        labColor?.a === '00.00' ? 0 : labColor?.a || 0
      } ${labColor?.b === '00.00' ? 0 : labColor?.b || 0})`,
    )
  }
  const handleDragStart = () => {
    if (!imageUrl) {
      return
    }
  }

  const handleDragEnd = (e: any) => {
    if (!imageUrl) {
      return
    }
    setPosition({ x: e.target.x(), y: e.target.y() })
  }

  useEffect(() => {
    dispatch(
      setImageEdit({
        imageEdit: false,
        placement: undefined,
        path: location.pathname,
        clearAll: true,
      }),
    )
  }, [location.pathname, dispatch])

  const handleImageEdit = () => {
    const selectedImage = isDualCanvas
      ? dualCanvasImage?.[placement]
      : canvasImage

    if (!selectedImage) return toast.error('Please select an image.')

    if (selectedImage?.type === imageType.OPTISHADE) {
      return toast.error('Edit image is not supported in optishade image.')
    }

    if (
      selectedImage?.type === imageType.RAW &&
      !selectedImage?.calibrated_image
    )
      return toast.error('Image is not calibrated.')
    dispatch(
      setImageEdit({ imageEdit: true, placement, path: location.pathname }),
    )
  }

  useEffect(() => {
    if (imageRef.current) {
      if (currentImageGrayscale && isShadeGuidePath) {
        imageRef.current.cache()
      } else {
        imageRef.current.clearCache()
      }
      imageRef.current.getLayer()?.batchDraw()
    }
  }, [currentImageGrayscale, isShadeGuidePath, image, imageSize, scale])

  return (
    <Box sx={{ position: 'relative', width, height }}>
      {!imageEdit && (
        <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 10 }}>
          <Button
            label={<img src={cropicon} alt="Crop" width={24} height={24} />}
            variant="contained"
            sx={{
              bgcolor: theme.palette.primary.main,
              borderRadius: '50%',
              width: 40,
              height: 40,
              minWidth: 0,
              padding: 1,
            }}
            onClick={handleImageEdit}
            disabled={!image}
          />
        </Box>
      )}

      {!imageUrl ? (
        <Box
          {...getRootProps()}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '200%',
            backgroundPosition: '52% 50%',
            backgroundColor: theme.palette.background.canvas,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            zIndex: 5,
            cursor: 'pointer',
          }}
        >
          <input {...getInputProps()} />
          <Box
            component="img"
            src={imageBg}
            alt="Background"
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 0,
              maxWidth: '100%',
              height: 'auto',
              pointerEvents: 'none',
            }}
          />
        </Box>
      ) : (
        <Box
          ref={combinedRef}
          sx={{
            filter:
              currentImageGrayscale && isShadeGuidePath
                ? 'grayscale(100%)'
                : 'none',
          }}
        >
          <Stage
            width={width}
            height={height}
            ref={stageRef}
            onWheel={handleWheel}
            onClick={handleClick}
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            scaleX={scale}
            scaleY={scale}
            x={position.x}
            y={position.y}
            onMouseMove={handleMouseMove}
            style={{ background: theme.palette.background.canvas }}
          >
            <Layer>
              <Image
                image={image}
                height={imageSize.height}
                width={imageSize.width}
                ref={imageRef}
                // {...(type === imageType.OPTISHADE && {
                //   width: imageSize.width,
                // })}
                onMouseEnter={(e) => {
                  const stage = e.target.getStage()
                  if (!stage || !imageId) return
                  onHoverChange?.(true)

                  if (
                    imageCalibrationStatus?.[imageId] ===
                      ImageCalibrationStatus.CALIBRATING &&
                    type === imageType.RAW
                  ) {
                    stage.container().style.cursor = 'crosshair'
                  } else if (
                    recipeGenerationStatus === RecipeGenerationStatus.GENERATING
                  ) {
                    stage.container().style.cursor = 'progress'
                  } else {
                    stage.container().style.cursor = 'default'
                  }
                }}
                onMouseLeave={(e) => {
                  onHoverChange?.(false)
                  const stage = e.target.getStage()
                  if (stage) stage.container().style.cursor = 'default'
                }}
              />

              {isActiveCircle &&
                activeCirclePos &&
                activeCanvas === placement && (
                  <Circle
                    x={activeCirclePos.x}
                    y={activeCirclePos.y}
                    radius={20 / scale}
                    fill="transparent"
                    stroke={activeCircleColor}
                    strokeWidth={5 / scale}
                    shadowColor="rgba(0,0,0,0.5)"
                    shadowBlur={8 / scale}
                    shadowOpacity={0.6}
                    shadowOffset={{ x: 2 / scale, y: 2 / scale }}
                    listening={false}
                  />
                )}

              {konvaElements?.map((el, index) => (
                <Group key={el.id || index} x={el.x ?? 0} y={el.y ?? 0}>
                  {el.component}
                </Group>
              ))}
              <KonavaElements
                hiddenCanvasRef={hiddenCanvasRef}
                placement={placement}
                type={type}
              />
            </Layer>
          </Stage>
        </Box>
      )}
    </Box>
  )
}

export default NewKonvaCanvas
